import React, {useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import {
    COURSE_STATUSES,
    COURSE_STATUS_LABELS,
    COURSE_STATUS_COLORS,
    validateUpcomingCourse,
} from "./courseValidation";

const formatDateNorwegian = (dateStr) => {
    if (!dateStr) return "";
    try {
        const [year, month, day] = dateStr.split("-");
        if (!year || !month || !day) return dateStr;
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        return date.toLocaleDateString("nb-NO", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return dateStr;
    }
};

const initialCourseState = {
    title: "",
    startDate: "",
    location: "",
    status: COURSE_STATUSES.OPEN,
    description: "",
    registrationUrl: "",
};

export const InstructorCoursesManager = ({
    courses = [],
    onSaveCourses,
    defaultLocation = "",
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCourseId, setEditingCourseId] = useState(null);
    const [formData, setFormData] = useState(initialCourseState);
    const [formErrors, setFormErrors] = useState({});
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveFeedback, setSaveFeedback] = useState(null);

    const handleOpenAdd = () => {
        setEditingCourseId(null);
        setFormData({
            ...initialCourseState,
            location: defaultLocation || "",
        });
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleOpenEdit = (course) => {
        setEditingCourseId(course.id);
        setFormData({
            title: course.title || "",
            startDate: course.startDate || "",
            location: course.location || defaultLocation || "",
            status: course.status || COURSE_STATUSES.OPEN,
            description: course.description || "",
            registrationUrl: course.registrationUrl || "",
        });
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCourseId(null);
        setFormErrors({});
    };

    const handleSaveCourse = async () => {
        const validation = validateUpcomingCourse(formData);
        if (!validation.isValid) {
            setFormErrors(validation.errors);
            return;
        }

        setIsSaving(true);
        setSaveFeedback(null);

        try {
            let updatedCourses = [];
            if (editingCourseId) {
                updatedCourses = courses.map((c) =>
                    c.id === editingCourseId
                        ? {...c, ...formData, updatedAt: new Date().toISOString()}
                        : c
                );
            } else {
                const newCourse = {
                    ...formData,
                    id: `course_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                updatedCourses = [...courses, newCourse];
            }

            await onSaveCourses(updatedCourses);
            setSaveFeedback({severity: "success", message: "Kurskalenderen er oppdatert."});
            handleCloseDialog();
        } catch (err) {
            setSaveFeedback({
                severity: "error",
                message: err.message || "Kunne ikke lagre kurset. Prøv igjen.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCourse = async (courseId) => {
        setIsSaving(true);
        setSaveFeedback(null);

        try {
            const updatedCourses = courses.filter((c) => c.id !== courseId);
            await onSaveCourses(updatedCourses);
            setSaveFeedback({severity: "success", message: "Kurset ble fjernet."});
            setDeleteConfirmId(null);
        } catch (err) {
            setSaveFeedback({
                severity: "error",
                message: err.message || "Kunne ikke slette kurset.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{mt: 4, mb: 3}}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: {xs: "flex-start", sm: "center"},
                    justifyContent: "space-between",
                    flexDirection: {xs: "column", sm: "row"},
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, mb: 0.5}}>
                        <CalendarMonthIcon color="primary" sx={{fontSize: 26}} />
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{fontWeight: 700, color: "primary.main"}}
                        >
                            Kommende kurskull
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{maxWidth: 620}}>
                        Registrer datoer for neste kursrekke. Disse vises på profilen din og på kartet på
                        forsiden, slik at innbyggere kan se hvem som har planlagte kurs og melde interesse.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAdd}
                    sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 2,
                        minHeight: 44,
                        whiteSpace: "nowrap",
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    Legg til kurskull
                </Button>
            </Box>

            {saveFeedback && (
                <Alert
                    severity={saveFeedback.severity}
                    onClose={() => setSaveFeedback(null)}
                    sx={{mb: 2.5}}
                >
                    {saveFeedback.message}
                </Alert>
            )}

            {/* Courses List */}
            {courses.length > 0 ? (
                <Stack spacing={2}>
                    {courses.map((course) => (
                        <Card
                            key={course.id}
                            variant="outlined"
                            sx={{
                                borderRadius: 2.5,
                                borderColor: "rgba(40, 82, 56, 0.16)",
                                bgcolor: "#FFFFFF",
                                transition: "box-shadow 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 14px rgba(40, 82, 56, 0.08)",
                                },
                            }}
                        >
                            <CardContent sx={{p: {xs: 2, sm: 2.5}, "&:last-child": {pb: {xs: 2, sm: 2.5}}}}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: {xs: "flex-start", sm: "center"},
                                        justifyContent: "space-between",
                                        flexDirection: {xs: "column", sm: "row"},
                                        gap: 1.5,
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap"}}>
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            sx={{fontWeight: 700, color: "text.primary", fontSize: "1.05rem"}}
                                        >
                                            {course.title}
                                        </Typography>
                                        <Chip
                                            label={COURSE_STATUS_LABELS[course.status] || course.status}
                                            color={COURSE_STATUS_COLORS[course.status] || "default"}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                                ...(course.status === COURSE_STATUSES.FEW_SEATS && {
                                                    bgcolor: "#FEF3C7",
                                                    color: "#92400E",
                                                    border: "1px solid #FCD34D",
                                                }),
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5, alignSelf: {xs: "flex-end", sm: "center"}}}>
                                        <IconButton
                                            aria-label={`Rediger ${course.title}`}
                                            size="small"
                                            onClick={() => handleOpenEdit(course)}
                                            sx={{
                                                color: "primary.main",
                                                minWidth: 44,
                                                minHeight: 44,
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            aria-label={`Slett ${course.title}`}
                                            size="small"
                                            onClick={() => setDeleteConfirmId(course.id)}
                                            sx={{
                                                color: "error.main",
                                                minWidth: 44,
                                                minHeight: 44,
                                                "&:focus-visible": {
                                                    outline: "2px solid #d32f2f",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2.5, my: 1, color: "text.secondary", fontSize: "0.875rem"}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                        <EventIcon sx={{fontSize: 18, color: "primary.main"}} />
                                        <Typography variant="body2" sx={{fontWeight: 600}}>
                                            Oppstart: {formatDateNorwegian(course.startDate)}
                                        </Typography>
                                    </Box>

                                    {course.location && (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                            <PlaceIcon sx={{fontSize: 18, color: "primary.main"}} />
                                            <Typography variant="body2">
                                                {course.location}
                                            </Typography>
                                        </Box>
                                    )}

                                    {course.registrationUrl && (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                            <LinkIcon sx={{fontSize: 18, color: "primary.main"}} />
                                            <Typography
                                                variant="body2"
                                                component="a"
                                                href={course.registrationUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{color: "primary.main", textDecoration: "underline"}}
                                            >
                                                Påmeldingslenke
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {course.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{mt: 1, lineHeight: 1.5}}>
                                        {course.description}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "background.paper",
                        borderRadius: 3,
                        border: "1px dashed rgba(40, 82, 56, 0.25)",
                    }}
                >
                    <CalendarMonthIcon sx={{fontSize: 40, color: "text.secondary", mb: 1, opacity: 0.6}} />
                    <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 0.5}}>
                        Ingen planlagte kurs registrert
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{maxWidth: 460, mx: "auto", mb: 2}}>
                        Legg til dato for din neste kursrekke. Kursene dine vil da vises med en egen uthevet badge
                        på kartet og i listen på forsiden.
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: 2,
                            minHeight: 44,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Legg til ditt første kurskull
                    </Button>
                </Box>
            )}

            {/* Add/Edit Course Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                aria-labelledby="course-dialog-title"
            >
                <DialogTitle id="course-dialog-title" sx={{fontWeight: 700, color: "primary.main"}}>
                    {editingCourseId ? "Rediger kurskull" : "Legg til nytt kurskull"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{mt: 0.5}}>
                        <TextField
                            label="Kurstittel"
                            placeholder="f.eks. Kurs i bekymringsmestring – Vårkull"
                            value={formData.title}
                            onChange={(e) => {
                                setFormData({...formData, title: e.target.value});
                                if (formErrors.title) setFormErrors({...formErrors, title: null});
                            }}
                            error={Boolean(formErrors.title)}
                            helperText={formErrors.title}
                            required
                            fullWidth
                            size="small"
                        />

                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
                            <TextField
                                label="Oppstartsdato"
                                type="date"
                                InputLabelProps={{shrink: true}}
                                value={formData.startDate}
                                onChange={(e) => {
                                    setFormData({...formData, startDate: e.target.value});
                                    if (formErrors.startDate) setFormErrors({...formErrors, startDate: null});
                                }}
                                error={Boolean(formErrors.startDate)}
                                helperText={formErrors.startDate}
                                required
                                fullWidth
                                size="small"
                            />

                            <TextField
                                select
                                label="Kapasitetsstatus"
                                value={formData.status}
                                onChange={(e) => setFormData({...formData, status: e.target.value})}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value={COURSE_STATUSES.OPEN}>Ledige plasser</MenuItem>
                                <MenuItem value={COURSE_STATUSES.FEW_SEATS}>Få plasser igjen</MenuItem>
                                <MenuItem value={COURSE_STATUSES.FULL}>Fulltegnet</MenuItem>
                                <MenuItem value={COURSE_STATUSES.COMPLETED}>Gjennomført</MenuItem>
                            </TextField>
                        </Box>

                        <TextField
                            label="Oppmøtested / Lokasjon"
                            placeholder="f.eks. Frisklivssentralen i Lillestrøm eller Digitalt"
                            value={formData.location}
                            onChange={(e) => {
                                setFormData({...formData, location: e.target.value});
                                if (formErrors.location) setFormErrors({...formErrors, location: null});
                            }}
                            error={Boolean(formErrors.location)}
                            helperText={formErrors.location}
                            required
                            fullWidth
                            size="small"
                        />

                        <TextField
                            label="Påmeldingslenke (valgfritt)"
                            placeholder="https://kommune.no/pamelding"
                            value={formData.registrationUrl}
                            onChange={(e) => {
                                setFormData({...formData, registrationUrl: e.target.value});
                                if (formErrors.registrationUrl) setFormErrors({...formErrors, registrationUrl: null});
                            }}
                            error={Boolean(formErrors.registrationUrl)}
                            helperText={formErrors.registrationUrl || "Lenke til kommunens eller virksomhetens påmeldingsside"}
                            fullWidth
                            size="small"
                        />

                        <TextField
                            label="Beskrivelse og praktisk info (valgfritt)"
                            placeholder="f.eks. 6 samlinger hver tirsdag kl. 17:00–19:00. Ta med skrivesaker."
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => {
                                setFormData({...formData, description: e.target.value});
                                if (formErrors.description) setFormErrors({...formErrors, description: null});
                            }}
                            error={Boolean(formErrors.description)}
                            helperText={formErrors.description}
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, py: 2}}>
                    <Button
                        onClick={handleCloseDialog}
                        disabled={isSaving}
                        sx={{
                            textTransform: "none",
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveCourse}
                        disabled={isSaving}
                        sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            minWidth: 100,
                            minHeight: 44,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : "Lagre kurs"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={Boolean(deleteConfirmId)}
                onClose={() => setDeleteConfirmId(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{fontWeight: 700}}>Fjerne kurskull?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Er du sikker på at du vil fjerne dette kurset fra kurskalenderen? Handlingen kan ikke angres.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{px: 2.5, py: 1.5}}>
                    <Button
                        onClick={() => setDeleteConfirmId(null)}
                        disabled={isSaving}
                        sx={{
                            textTransform: "none",
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteCourse(deleteConfirmId)}
                        disabled={isSaving}
                        sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            minHeight: 44,
                            "&:focus-visible": {
                                outline: "2px solid #d32f2f",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : "Fjern kurs"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
